import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { REPORT_TYPE } from '../../../../utils/constants';
import { getFeedbackReport, getFeedbackReportId } from '../../../../api/feedback-report';
import { useAuth } from '../../../../providers/auth-provider';

export const FeedbackReport = ({ candidate, reportType, creatingPdf, wordsPerPage = 300 }) => {
  const auth = useAuth();
  const isDevelop = reportType === REPORT_TYPE.development;
  const [firstName, setFirstName] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (candidate) {
      setIsLoading(true);
      getFeedbackReportId(auth, candidate, isDevelop).then((feedbackReportId) => {
        if (feedbackReportId) {
          getFeedbackReport(auth, feedbackReportId).then((report) => {
            setReportData(report);
            setFirstName(candidate?.name?.split(' ')?.[0] || '');
            setIsLoading(false);
          });
        }
      });
    }
  }, [candidate]);

  const themesSorted =
    reportData?.developmentalThemes?.themes?.slice()?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];

  // Compute intro and tChart text based on report type
  let intro = '';
  if (isDevelop) {
    intro = `Based on decades of research, this report focuses on the areas of leadership that directly drive
              important business outcomes such as performance and retention. These are the areas of the highest
              prioritization for there to be a true return-on-investment for development. As ${firstName} builds a
              development plan, these areas of focus will have the biggest impact.`;
  } else {
    intro = `Based on decades of research, this report focuses on the critical areas that directly drive important
              business outcomes, such as performance and retention. These are the factors of the highest prioritization
              for evaluating candidate fit and making informed selection decisions. As the organization considers
              ${firstName} for the role, these areas of focus will have the biggest impact on retention and potential
              for success.`;
  }

  let tChartText = '';
  if (isDevelop) {
    tChartText =
      'The following table highlights key strengths to leverage and critical areas for development based on assessment data and multi-source feedback.';
  } else {
    tChartText =
      'The following table highlights key strengths to leverage and critical areas for candidate development based on assessment data.';
  }

  /**
   * Base words-per-page budget for pagination.
   *
   * Notes:
   * - This is an approximation: visual layout ultimately depends on CSS and fonts.
   * - Headings do not contribute to the word budget separately; they render together with
   *   the first paragraph they label to avoid orphan headers at the bottom of pages.
   */
  const WORDS_PER_PAGE = Number(wordsPerPage);
  /**
   * Overflow allowance applied only to pages AFTER the first page that contains the T-Chart.
   *
   * Rationale:
   * - The first T-Chart page is capped at the base `WORDS_PER_PAGE` to preserve visual balance.
   * - Later pages can stretch up to +20% to improve packing efficiency without noticeably
   *   degrading readability.
   *
   * This is intentionally NOT a prop; tweak here to adjust global layout.
   */
  const OVERFLOW_ALLOWANCE_AFTER_TCHART = 0.6; // 40%

  /**
   * Count words in a string using a simple whitespace-based heuristic.
   *
   * Implementation details:
   * - Normalizes all contiguous whitespace to single spaces, trims ends,
   *   and splits on space characters.
   * - Filters out empty tokens to avoid counting artifacts.
   *
   * This intentionally does not attempt to handle special hyphenation or punctuation rules;
   * it is sufficient as a stable metric to drive pagination.
   *
   * @param {string} txt
   * @returns {number} word count (>= 0)
   */
  const countWords = (txt) => {
    if (!txt) return 0;
    return String(txt)
      .replace(/\s+/g, ' ') // normalize whitespace
      .trim()
      .split(' ')
      .filter(Boolean).length;
  };

  /**
   * Build a unified, linear sequence of render items (content blocks) in reading order
   * along with their word-costs used for pagination.
   *
   * Item taxonomy (discriminated by `type`):
   * - introParagraph: paragraphs that must appear on page 1 (hard-coded intro) or early intro content.
   *   { type: 'introParagraph', key, text, words, forcePage1? }
   *     - `forcePage1` is true only for the hard-coded intro paragraph(s) and guarantees they render
   *       on the first page even if they overrun the budget (their words still consume page-1 budget).
   * - tchartRow: a paired left/right row for Key Strengths vs. Development Needs.
   *   { type: 'tchartRow', key, left, right, words }
   *     - `words` equals max( words(left title+bullets), words(right title+bullets) )
   *       so that the column with the most text governs space consumption.
   *     - Left/right data are kept together and never split across pages.
   * - sectionParagraph: narrative paragraphs in sections and themes.
   *   { type: 'sectionParagraph', key, section, sectionTitle, themeTitle|null, text, words }
   *     - Headings (sectionTitle/themeTitle) are not counted separately; they render together with
   *       the first paragraph of their block on the page where that paragraph appears.
   *
   * Invariants:
   * - Paragraphs are indivisible units for pagination; if one doesn’t fit, it moves entirely to the next page.
   * - Headers/titles never appear as the last line of a page without at least one of their paragraphs.
   * - T-Chart rows maintain parallel grouping of left/right content across pages.
   *
   * @returns {Array<IntroParagraphItem|TChartRowItem|SectionParagraphItem>}
   */
  const buildRenderItems = () => {
    if (!reportData) return [];

    const items = [];

    // Introductory content (hardcoded + dynamic intro)
    items.push({
      type: 'introParagraph',
      key: `hard-intro-0`,
      text: intro,
      words: countWords(intro),
      forcePage1: true,
    });

    items.push({ type: 'introParagraph', key: 'tchart-expl', text: tChartText, words: countWords(tChartText) });

    // T-Chart rows as paired items (parallel grouping kept together)
    if (reportData.tChart) {
      const left = (reportData.tChart.keyStrengths || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      const right = (reportData.tChart.developmentNeeds || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      const maxLen = Math.max(left.length, right.length);
      for (let i = 0; i < maxLen; i++) {
        const l = left[i] || {};
        const r = right[i] || {};
        const leftText = [l.title, ...(l.bullets || [])].filter(Boolean).join(' ');
        const rightText = [r.title, ...(r.bullets || [])].filter(Boolean).join(' ');
        const words = Math.max(countWords(leftText), countWords(rightText));
        items.push({
          type: 'tchartRow',
          key: `trow-${i}-${l.order || l.title || 'L'}-${r.order || r.title || 'R'}`,
          left: l,
          right: r,
          words,
        });
      }
    }

    // Narrative sections
    const pushSectionParagraph = (section, sectionTitle, themeTitle, text, idxKey) => {
      items.push({
        type: 'sectionParagraph',
        key: `${section}-${idxKey}`,
        section,
        sectionTitle,
        themeTitle: themeTitle || null,
        text,
        words: countWords(text),
      });
    };

    // Strengths to Leverage
    const stl = reportData.strengthsToLeverage;
    if (stl?.paragraphs?.length) {
      const sectionTitle = stl.sectionTitle || 'Strengths to Leverage';
      stl.paragraphs.forEach((text, idx) => pushSectionParagraph('strengths', sectionTitle, null, text, `stl-${idx}`));
    }

    // Developmental Themes
    const dt = reportData.developmentalThemes;
    if (dt?.themes?.length) {
      const sectionTitle = dt.sectionTitle || 'Developmental Themes';
      dt.themes
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach((theme) => {
          const themeTitle = theme.title;
          (theme.paragraphs || []).forEach((text, idx) =>
            pushSectionParagraph('themes', sectionTitle, themeTitle, text, `theme-${theme.order || theme.title}-${idx}`)
          );
        });
    }

    // Conclusion and Next Steps
    const cns = reportData.conclusionAndNextSteps;
    if (cns?.paragraphs?.length) {
      const sectionTitle = cns.sectionTitle || 'Conclusion and Next Steps';
      cns.paragraphs.forEach((text, idx) => pushSectionParagraph('conclusion', sectionTitle, null, text, `cns-${idx}`));
    }

    return items;
  };

  const renderItems = buildRenderItems();

  /**
   * Compute pagination over the linear `renderItems` sequence using the following rules:
   *
   * Budgeting:
   * - Base limit per page: `WORDS_PER_PAGE`.
   * - Find the first page that contains any T-Chart rows; that page is strictly capped at the base limit.
   * - All pages AFTER that first T-Chart page may use an overflow budget of
   *   `WORDS_PER_PAGE * (1 + OVERFLOW_ALLOWANCE_AFTER_TCHART)`.
   * - Pages before the T-Chart (e.g., intro-only pages) use the base limit.
   *
   * Grouping and placement:
   * - Items represent indivisible units (intro paragraph, tchart row, section paragraph). If adding an item
   *   would exceed the current page’s limit, we start a new page and place it there.
   * - Hard-coded intro paragraphs flagged as `forcePage1` are always rendered on page 1 even if they exceed
   *   the page-1 limit; however, their words still count against that budget.
   * - Section and theme headers render in tandem with the first paragraph shown on a page but do not consume
   *   words themselves. Continuation labels “(continued)” are handled during rendering and do not affect the
   *   pagination budget.
   *
   * Implementation notes:
   * - We track `firstTChartPageIdx` while placing items to know when to allow the overflow budget on later pages.
   * - `current.used` accumulates the sum of item `words` placed on the page; this is a logical metric and
   *    not a guarantee of exact line counts.
   */
  const paginated = (() => {
    const pages = [];
    let current = { items: [], used: 0, index: 0 };
    let firstTChartPageIdx = null; // track the index of the first page that contains tChart rows

    const pushPage = () => {
      pages.push(current);
      current = { items: [], used: 0, index: pages.length };
    };

    // Ensure hardcoded intro stays on page 1 regardless of its cost
    // We still count its words towards the budget for page 1
    for (const it of renderItems) {
      const isForcedFirstPage = it.type === 'introParagraph' && it.forcePage1;
      // Determine the current page limit based on whether we've already encountered the T-Chart
      const baseLimit = WORDS_PER_PAGE;
      const overflowLimit = Math.floor(WORDS_PER_PAGE * (1 + OVERFLOW_ALLOWANCE_AFTER_TCHART));
      const isAfterFirstTChartPage = firstTChartPageIdx !== null && current.index > firstTChartPageIdx;
      const currentLimit = isAfterFirstTChartPage ? overflowLimit : baseLimit;

      // Decide if the item fits on the current page under the active limit, or is a forced page-1 intro.
      const fits = current.used + it.words <= currentLimit || (current.index === 0 && isForcedFirstPage);

      if (!fits) {
        // Start a new page. Headings remain attached to their paragraphs implicitly because
        // headers are rendered together with the first paragraph of a block.
        if (current.items.length > 0) pushPage();
        // Recompute currentLimit for the new page (index has changed) in the next loop iteration.
      }

      // If it's forced first page but we're not on first page, move to first page
      if (isForcedFirstPage && current.index !== 0 && pages.length > 0) {
        // place it back onto the first page at the start
        pages[0].items.unshift(it);
        pages[0].used += it.words;
        continue;
      }

      current.items.push(it);
      current.used += it.words;

      // If this item is a T-Chart row and we haven't yet marked the first T-Chart page, do so now
      if (it.type === 'tchartRow' && firstTChartPageIdx === null) {
        firstTChartPageIdx = current.index;
      }
    }

    if (current.items.length > 0) pushPage();
    return pages;
  })();

  return (
    <>
      {!creatingPdf ? (
        // Single-page view (no explicit print-page wrappers)
        <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
          {reportData ? (
            <Container className="candidate-profile-assessment-content">
              <h3 className="pt-4 pb-1">FEEDBACK OVERVIEW</h3>
              <p>{intro}</p>

              {reportData.tChart && (
                <>
                  <p className="mt-3">{tChartText}</p>
                  <div className="mt-4 p-3">
                    <Row className="mt-3 gx-5">
                      <Col md={6} className="mb-3 pe-4">
                        <h5 className="mb-2">Key Strengths</h5>
                        {reportData.tChart.keyStrengths
                          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((item) => (
                            <div key={`strength-${item.order || item.title}`} className="mb-3">
                              <div className="fw-semibold">{item.title}</div>
                              <ul className="mb-0">
                                {item.bullets?.map((bullet, idx) => (
                                  <li key={`strength-${item.order || item.title}-${idx}`}>{bullet}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                      </Col>
                      <Col
                        md={6}
                        className="mb-3 border-start border-2 ps-4"
                        style={{ '--bs-border-color': '#0770a9', '--bs-border-opacity': 1 }}
                      >
                        <h5 className="mb-2">Development Needs</h5>
                        {reportData.tChart.developmentNeeds
                          ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((item) => (
                            <div key={`dev-${item.order || item.title}`} className="mb-3">
                              <div className="fw-semibold">{item.title}</div>
                              <ul className="mb-0">
                                {item.bullets?.map((bullet, idx) => (
                                  <li key={`dev-${item.order || item.title}-${idx}`}>{bullet}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </div>
                </>
              )}

              {reportData.strengthsToLeverage && (
                <>
                  <h4 className="mt-4">{reportData.strengthsToLeverage.sectionTitle || 'Strengths to Leverage'}</h4>
                  {reportData.strengthsToLeverage.paragraphs?.map((p, idx) => (
                    <p key={`stl-${idx}`}>{p}</p>
                  ))}
                </>
              )}

              {reportData.developmentalThemes && (
                <>
                  <h4 className="mt-4">{reportData.developmentalThemes.sectionTitle || 'Developmental Themes'}</h4>
                  {themesSorted.map((theme) => (
                    <div key={`theme-${theme.order || theme.title}`} className="mb-3">
                      <div className="fw-semibold">{theme.title}</div>
                      {theme.paragraphs?.map((p, idx) => (
                        <p key={`theme-${theme.order || theme.title}-${idx}`}>{p}</p>
                      ))}
                    </div>
                  ))}
                </>
              )}

              {reportData.conclusionAndNextSteps && (
                <>
                  <h4 className="mt-4">
                    {reportData.conclusionAndNextSteps.sectionTitle || 'Conclusion and Next Steps'}
                  </h4>
                  {reportData.conclusionAndNextSteps.paragraphs?.map((p, idx) => (
                    <p key={`cns-${idx}`}>{p}</p>
                  ))}
                </>
              )}
            </Container>
          ) : isLoading ? (
            <Container className="candidate-profile-assessment-content text-center py-5">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <Spinner animation="border" role="status" variant="primary" className="mb-3">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <h4 className="mb-2">Generating Feedback Overview Report</h4>
                <p className="mb-0 text-muted">
                  We&apos;re compiling responses and insights for this report. It will appear here automatically as soon
                  as it&apos;s ready.
                </p>
              </div>
            </Container>
          ) : (
            <Container className="candidate-profile-assessment-content text-center py-5">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <Spinner animation="border" role="status" variant="primary" className="mb-3">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <h4 className="mb-2">Report Generation In Progress</h4>
                <p className="mb-0 text-muted">
                  This report is taking a bit longer than usual to generate. Please return back soon for the completed
                  report.
                </p>
              </div>
            </Container>
          )}
        </Container>
      ) : (
        // Multi-page PDF view
        <>
          {/* Page 1: Render from paginated[0] respecting word budget and grouping */}
          <div className="print-page">
            <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
              {reportData ? (
                <Container className="candidate-profile-assessment-content">
                  <h3 className="pt-4 pb-1">FEEDBACK OVERVIEW</h3>
                  {(() => {
                    const first = paginated[0] || { items: [] };
                    const rows = first.items.filter((it) => it.type === 'tchartRow');
                    const introParas = first.items.filter((it) => it.type === 'introParagraph');
                    const sectionParas = first.items.filter((it) => it.type === 'sectionParagraph');
                    const shownSections = new Set();
                    const shownThemes = new Set();
                    return (
                      <>
                        {introParas.map((p) => (
                          <p key={p.key}>{p.text}</p>
                        ))}

                        {rows.length > 0 && (
                          <div className="mt-4 p-3">
                            <Row className="mt-3 gx-5">
                              <Col md={6} className="mb-3 pe-4">
                                <h5 className="mb-2">Key Strengths</h5>
                                {rows.map((row, rIdx) => (
                                  <div key={`row-left-${row.key}-${rIdx}`} className="mb-3">
                                    <div className="fw-semibold">{row.left?.title}</div>
                                    <ul className="mb-0">
                                      {(row.left?.bullets || []).map((b, bi) => (
                                        <li key={`row-left-${row.key}-b-${bi}`}>{b}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </Col>
                              <Col
                                md={6}
                                className="mb-3 border-start border-2 ps-4"
                                style={{ '--bs-border-color': '#0770a9', '--bs-border-opacity': 1 }}
                              >
                                <h5 className="mb-2">Development Needs</h5>
                                {rows.map((row, rIdx) => (
                                  <div key={`row-right-${row.key}-${rIdx}`} className="mb-3">
                                    <div className="fw-semibold">{row.right?.title}</div>
                                    <ul className="mb-0">
                                      {(row.right?.bullets || []).map((b, bi) => (
                                        <li key={`row-right-${row.key}-b-${bi}`}>{b}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </Col>
                            </Row>
                          </div>
                        )}

                        {sectionParas.map((item, idx) => {
                          const elems = [];
                          if (item.sectionTitle && !shownSections.has(item.sectionTitle)) {
                            elems.push(
                              <h4 className="mt-4" key={`sec-first-${item.section}-${idx}`}>
                                {item.sectionTitle}
                              </h4>
                            );
                            shownSections.add(item.sectionTitle);
                          }
                          if (item.section === 'themes' && item.themeTitle && !shownThemes.has(item.themeTitle)) {
                            elems.push(
                              <div className="fw-semibold" key={`theme-title-first-${item.themeTitle}-${idx}`}>
                                {item.themeTitle}
                              </div>
                            );
                            shownThemes.add(item.themeTitle);
                          }
                          elems.push(<p key={`p-first-${item.key}`}>{item.text}</p>);
                          return elems;
                        })}
                      </>
                    );
                  })()}
                </Container>
              ) : isLoading ? (
                <Container className="candidate-profile-assessment-content text-center py-5">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <h4 className="mb-2">Generating your Feedback Overview</h4>
                    <p className="mb-0 text-muted">
                      Your report content is being prepared and will appear here once generation is complete.
                    </p>
                  </div>
                </Container>
              ) : (
                <Container className="candidate-profile-assessment-content text-center py-5">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <h4 className="mb-2">Report Generation In Progress</h4>
                    <p className="mb-0 text-muted">
                      This report is taking a bit longer than usual to generate. Please return back soon for the
                      completed report.
                    </p>
                  </div>
                </Container>
              )}
            </Container>
          </div>

          {/* Subsequent pages: paginate based on word count */}
          {reportData &&
            paginated.length > 1 &&
            (() => {
              // Track sections/themes that have appeared on prior pages, to mark continuations
              const seenSections = new Set();
              const seenThemes = new Set();

              // Track whether the T-Chart has already appeared on any previous page
              let tchartSeenBefore = (paginated[0] || { items: [] }).items.some((it) => it.type === 'tchartRow');

              // Preload with what appears on the first page
              const firstPage = paginated[0] || { items: [] };
              firstPage.items
                .filter((it) => it.type === 'sectionParagraph')
                .forEach((it) => {
                  if (it.sectionTitle) seenSections.add(it.sectionTitle);
                  if (it.section === 'themes' && it.themeTitle) seenThemes.add(it.themeTitle);
                });

              return paginated.slice(1).map((page, pageIdx) => {
                // Track which section/theme titles have been shown on this specific page
                const shownSections = new Set();
                const shownThemes = new Set();

                // Collect tChart rows and paragraphs for this page
                const rows = page.items.filter((it) => it.type === 'tchartRow');
                const introParas = page.items.filter((it) => it.type === 'introParagraph');
                const sectionParas = page.items.filter((it) => it.type === 'sectionParagraph');

                const hasTchartRows = rows.length > 0;

                // Compute titles that appear on this page (to update seen sets after rendering)
                const pageSectionTitles = new Set(sectionParas.map((it) => it.sectionTitle).filter(Boolean));
                const pageThemeTitles = new Set(
                  sectionParas
                    .filter((it) => it.section === 'themes')
                    .map((it) => it.themeTitle)
                    .filter(Boolean)
                );

                const rendered = (
                  <div className="print-page" key={`pdf-page-${pageIdx + 1}`}>
                    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
                      <Container className="candidate-profile-assessment-content">
                        <h3 className="pt-4 pb-1">FEEDBACK OVERVIEW (continued)</h3>

                        {introParas.map((p) => (
                          <p key={p.key}>{p.text}</p>
                        ))}

                        {hasTchartRows && (
                          <div className="mt-4 p-3">
                            <Row className="mt-3 gx-5">
                              <Col md={6} className="mb-3 pe-4">
                                <h5 className="mb-2">
                                  {tchartSeenBefore ? 'Key Strengths (continued)' : 'Key Strengths'}
                                </h5>
                                {rows.map((row, rIdx) => (
                                  <div key={`row-left-${row.key}-${rIdx}`} className="mb-3">
                                    <div className="fw-semibold">{row.left?.title}</div>
                                    <ul className="mb-0">
                                      {(row.left?.bullets || []).map((b, bi) => (
                                        <li key={`row-left-${row.key}-b-${bi}`}>{b}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </Col>
                              <Col
                                md={6}
                                className="mb-3 border-start border-2 ps-4"
                                style={{ '--bs-border-color': '#0770a9', '--bs-border-opacity': 1 }}
                              >
                                <h5 className="mb-2">
                                  {tchartSeenBefore ? 'Development Needs (continued)' : 'Development Needs'}
                                </h5>
                                {rows.map((row, rIdx) => (
                                  <div key={`row-right-${row.key}-${rIdx}`} className="mb-3">
                                    <div className="fw-semibold">{row.right?.title}</div>
                                    <ul className="mb-0">
                                      {(row.right?.bullets || []).map((b, bi) => (
                                        <li key={`row-right-${row.key}-b-${bi}`}>{b}</li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </Col>
                            </Row>
                          </div>
                        )}

                        {sectionParas.map((item, idx) => {
                          const elems = [];
                          if (item.sectionTitle && !shownSections.has(item.sectionTitle)) {
                            const secContinued = seenSections.has(item.sectionTitle);
                            const secTitleText = secContinued ? `${item.sectionTitle} (continued)` : item.sectionTitle;
                            elems.push(
                              <h4 className="mt-4" key={`sec-${item.section}-${pageIdx}-${idx}`}>
                                {secTitleText}
                              </h4>
                            );
                            shownSections.add(item.sectionTitle);
                          }
                          if (item.section === 'themes' && item.themeTitle && !shownThemes.has(item.themeTitle)) {
                            const themeContinued = seenThemes.has(item.themeTitle);
                            const themeTitleText = themeContinued ? `${item.themeTitle} (continued)` : item.themeTitle;
                            elems.push(
                              <div className="fw-semibold" key={`theme-title-${item.themeTitle}-${pageIdx}-${idx}`}>
                                {themeTitleText}
                              </div>
                            );
                            shownThemes.add(item.themeTitle);
                          }
                          elems.push(<p key={`p-${item.key}`}>{item.text}</p>);
                          return elems;
                        })}
                      </Container>
                    </Container>
                  </div>
                );

                // Mark these titles as seen for subsequent pages
                pageSectionTitles.forEach((t) => seenSections.add(t));
                pageThemeTitles.forEach((t) => seenThemes.add(t));

                // After rendering this page, if it contained any T-Chart rows,
                // mark that T-Chart has been seen so later pages show "(continued)"
                if (hasTchartRows) tchartSeenBefore = true;

                return rendered;
              });
            })()}
        </>
      )}
    </>
  );
};
